import { Component } from "react"

/* Modal */
import Modal from "react-modal"

/* Components */
import { Icon, Avatar } from "../../components"

/* Icons */
import { LoadingOutlined } from "@ant-design/icons"

/* REST API */
import { get } from "../../api/Master"

/* Widgets */
import { toast } from "../common"

/* Shared */
import { roleOf, ratingText } from "./shared"
import { BOOKING_STATUSES, channelOf, ago, initialsOf, avatarColorOf, digitsOf } from "../user/shared"
import { organizationIcon } from "../organization/shared"
import { plural } from "../category/shared"
import { money } from "../service/shared"

/* Constants */
import { env } from "../../constants"

/* Moment */
import moment from "moment"

Modal.setAppElement("#root")


/* The keys a point gives to its specialist */
const ACCESSES = [
    { key: "accessPhone", text: "Телефоны" },
    { key: "accessBooking", text: "Записи" },
    { key: "accessSchedule", text: "Расписание" },
    { key: "notifyWhatsapp", text: "WhatsApp" }
]


/* Widget Master Modal — the whole card of one master of the platform: the points he works in and
   his role in each, the visits booked to him, the money they brought and what the clients say.
   It is opened by the reference: this._master.current.open(item).
   Props: onOrganization(id) — opens the card of the point; without it the points are not links */
class MasterModal extends Component {

    constructor() {
        super()

        this.state = {
            visible: false,
            status: "loading",
            data: null,

            /* The preview of the line of the list — the header is drawn while the card loads */
            item: null
        }
    }

    open = item => {
        this.setState({ visible: true, item, status: "loading", data: null }, () => this.load())
    }

    close = () => {
        this.setState({ visible: false })
    }

    load = () => {

        const { item } = this.state

        get({ id: item.id }).then(response => {
            if (response && response.status === 200) {
                this.setState({ status: "ok", data: response.data })
            }
            else {
                this.setState({ status: "error" })
            }
        }).catch(() => this.setState({ status: "error" }))
    }

    copy = text => {

        if (!navigator.clipboard) {
            return
        }

        navigator.clipboard.writeText(text).then(() => toast.success("Скопировано")).catch(() => { })
    }

    /* The point goes to its own page — the card closes first */
    organization = id => {

        const { onOrganization } = this.props

        if (!onOrganization) {
            return
        }

        this.setState({ visible: false })
        onOrganization(id)
    }

    /* The top of the card — the photo, the name, the roles and the ways to reach the master */
    _hero = () => {

        /* Fields */
        const { item, data } = this.state

        const member = data ? data.member : item
        const phone = digitsOf(member.phone)
        const image = member.image ? `${env.mediapoint}members/${member.image}` : null
        const name = member.name && `${member.name}`.trim().length > 0 ? `${member.name}`.trim() : null

        /* The roles of the living teams */
        const roles = data
            ? [...new Set(data.teams.filter(team => parseInt(team.active) === 1).map(team => team.role))]
            : ["owner", "admin", "specialist"].filter(key => parseInt(item[key]) > 0)

        return (
            <div className="user-hero" style={{ "--avatar": avatarColorOf(member.id) }}>

                <Avatar className="user-hero-avatar" src={image} alt={name}>
                    {name ? <span>{initialsOf(name)}</span> : <Icon name="master" size={36} width={1.8} />}
                </Avatar>

                <div className="user-hero-text">

                    <div className="user-hero-name">{name ? name : "Без имени"}</div>

                    <div className="user-hero-badges">
                        {roles.map(key => {
                            const role = roleOf(key)
                            return <div key={key} className={`user-badge ${role.accent}`}><Icon name={role.icon} size={12} width={2.2} />{role.text}</div>
                        })}
                        {roles.length === 0 && <div className="user-badge grey">Без команды</div>}
                        {parseInt(member.verified) === 1
                            ? <div className="user-badge green"><i />Входит в Pro</div>
                            : <div className="user-badge grey"><i />Код не введён</div>
                        }
                    </div>

                    <div className="user-hero-contacts">

                        {member.phone &&
                            <div onClick={() => this.copy(member.phone)} className="user-contact" title="Скопировать">
                                <Icon name="otp" size={14} width={2} />
                                {member.phone}
                            </div>
                        }

                        {phone.length >= 10 &&
                            <a href={`https://wa.me/${phone}`} target="_blank" rel="noreferrer" className="user-contact whatsapp" title="Написать в WhatsApp">
                                <Icon name="support" size={14} width={2} />
                                WhatsApp
                            </a>
                        }

                        {phone.length >= 10 &&
                            <a href={`tel:+${phone}`} className="user-contact" title="Позвонить">
                                <Icon name="power" size={14} width={2} />
                                Позвонить
                            </a>
                        }

                        {member.email &&
                            <a href={`mailto:${member.email}`} className="user-contact" title="Написать на почту">
                                <Icon name="notification" size={14} width={2} />
                                {member.email}
                            </a>
                        }

                    </div>

                </div>

            </div>
        )
    }

    /* The numbers of the master */
    _metrics = () => {

        /* Fields */
        const { data } = this.state

        const totals = data.totals
        const extra = data.extra
        const missed = totals.cancelled + totals.missed
        const share = totals.bookings > 0 ? Math.round(missed / totals.bookings * 100) : 0

        const cards = [
            { value: totals.bookings.toLocaleString("ru-RU"), text: plural(totals.bookings, "запись", "записи", "записей"), accent: "blue" },
            { value: totals.completed.toLocaleString("ru-RU"), text: "состоялись", accent: "green" },
            { value: money(totals.revenue), text: "принёс точкам", accent: "violet" },
            { value: extra.rating ? `★ ${ratingText(extra.rating)}` : "—", text: extra.reviews > 0 ? `${extra.reviews} ${plural(extra.reviews, "отзыв", "отзыва", "отзывов")}` : "нет отзывов", accent: "orange" }
        ]

        return (
            <div className="user-section">

                <div className="user-metrics">
                    {cards.map(card =>
                        <div className={`user-metric ${card.accent}`} key={card.text}>
                            <b>{card.value}</b>
                            <span>{card.text}</span>
                        </div>
                    )}
                </div>

                <div className="user-facts">
                    <div><span>В Bookify Pro с</span><b>{moment(data.member.createdAt).format("D MMMM YYYY")}</b></div>
                    <div><span>Последний визит</span><b>{totals.lastVisit ? `${moment(totals.lastVisit).format("D MMM YYYY")} · ${ago(totals.lastVisit)}` : "—"}</b></div>
                    <div><span>Клиентов · отмен</span><b>{totals.clients.toLocaleString("ru-RU")} · {share}%</b></div>
                </div>

            </div>
        )
    }

    /* The visits of the last six months — a bar a month, the finished part is darker */
    _months = () => {

        /* Fields */
        const { data } = this.state

        if (data.totals.bookings === 0) {
            return null
        }

        const months = []

        for (let index = 5; index >= 0; index--) {
            const month = moment().subtract(index, "months")
            const row = data.months.find(item => item.month === month.format("YYYY-MM"))
            months.push({ key: month.format("YYYY-MM"), text: month.format("MMM"), bookings: row ? row.bookings : 0, completed: row ? row.completed : 0 })
        }

        const top = Math.max(...months.map(month => month.bookings), 1)

        return (
            <div className="user-section">

                <div className="user-section-title">
                    Записи по месяцам
                    <span>{data.totals.upcoming > 0 ? `впереди ${data.totals.upcoming}` : "впереди ничего"}</span>
                </div>

                <div className="master-months">
                    {months.map(month =>
                        <div className="master-month" key={month.key} title={`${month.bookings} записей, состоялись ${month.completed}`}>
                            <b>{month.bookings > 0 ? month.bookings : ""}</b>
                            <div className="master-month-bar" style={{ height: `${Math.max(4, month.bookings / top * 100)}%` }}>
                                <i style={{ height: `${month.bookings > 0 ? month.completed / month.bookings * 100 : 0}%` }} />
                            </div>
                            <span>{month.text}</span>
                        </div>
                    )}
                </div>

            </div>
        )
    }

    /* The points of the master — the living teams first, the ones he left below */
    _teams = () => {

        /* Fields */
        const { data } = this.state
        const { onOrganization } = this.props

        if (data.teams.length === 0) {
            return (
                <div className="user-section">
                    <div className="user-section-title">Точки</div>
                    <div className="user-empty">Мастер не состоит ни в одной команде — вошёл в Pro, но точку не создал и приглашения не принял</div>
                </div>
            )
        }

        return (
            <div className="user-section">

                <div className="user-section-title">Точки<span>{data.teams.length}</span></div>

                <div className="master-teams">
                    {data.teams.map(team => {

                        const role = roleOf(team.role)
                        const active = parseInt(team.active) === 1

                        return (
                            <div
                                key={`${team.membershipID}`}
                                onClick={() => this.organization(team.id)}
                                className={`master-team ${active ? "" : "left"} ${onOrganization ? "link" : ""}`}
                            >

                                <Avatar className="user-point-avatar" src={team.image ? `${env.mediapoint}organizations/${team.image}` : null} alt={team.name}>
                                    {organizationIcon()}
                                </Avatar>

                                <div className="master-team-text">
                                    <div className="master-team-name">
                                        <b>{team.name}</b>
                                        {!active && <em>Ушёл из команды</em>}
                                    </div>
                                    <div className="master-team-meta">
                                        <span className={`master-role ${role.accent}`}><Icon name={role.icon} size={11} width={2.2} />{role.text}</span>
                                        {team.profession && <span>{team.profession}</span>}
                                        <span>команда {team.team}</span>
                                        {parseInt(team.inSchedule) === 0 && <span>не в расписании</span>}
                                    </div>
                                    {team.role === "specialist" && active &&
                                        <div className="master-team-access">
                                            {ACCESSES.map(access =>
                                                <i key={access.key} className={parseInt(team[access.key]) === 1 ? "on" : ""}>{access.text}</i>
                                            )}
                                        </div>
                                    }
                                </div>

                                <div className="master-team-numbers">
                                    <b>{team.bookings.toLocaleString("ru-RU")}</b>
                                    <span>{money(team.revenue)}</span>
                                </div>

                                {onOrganization && <div className="master-team-arrow"><Icon name="right" size={14} width={2} /></div>}

                            </div>
                        )
                    })}
                </div>

            </div>
        )
    }

    /* The last visits booked to the master */
    _bookings = () => {

        /* Fields */
        const { data } = this.state

        if (data.bookings.length === 0) {
            return null
        }

        return (
            <div className="user-section">

                <div className="user-section-title">
                    Последние записи
                    {data.totals.bookings > data.bookings.length && <span>{data.bookings.length} из {data.totals.bookings}</span>}
                </div>

                <div className="user-timeline">
                    {data.bookings.map(item => {

                        const state = BOOKING_STATUSES[item.status] ? BOOKING_STATUSES[item.status] : BOOKING_STATUSES[0]
                        const channel = channelOf(item.channel)
                        const date = moment(item.reservedTime)

                        return (
                            <div className={`user-booking ${state.accent}`} key={`${item.id}`}>

                                <div className="user-booking-date">
                                    <b>{date.format("D")}</b>
                                    <span>{date.format("MMM YY")}</span>
                                </div>

                                <div className="user-booking-body">
                                    <div className="user-booking-title">
                                        {item.client && `${item.client}`.trim() ? `${item.client}`.trim() : "Клиент без имени"}
                                        <div className={`user-booking-status ${state.accent}`}>{state.text}</div>
                                    </div>
                                    <div className="user-booking-services">{item.services ? item.services : "Услуга не указана"}</div>
                                    <div className="user-booking-meta">
                                        <span>{date.format("HH:mm")}</span>
                                        {item.organization && <span>{item.organization}</span>}
                                        <span className="channel" style={{ "--channel": channel.color }}><i />{channel.text}</span>
                                    </div>
                                </div>

                                <div className="user-booking-price">{money(item.price)}</div>

                            </div>
                        )
                    })}
                </div>

            </div>
        )
    }

    /* What the clients say about the master */
    _reviews = () => {

        /* Fields */
        const { data } = this.state

        if (data.reviews.length === 0) {
            return null
        }

        return (
            <div className="user-section">

                <div className="user-section-title">Отзывы<span>{data.extra.reviews}</span></div>

                <div className="user-reviews">
                    {data.reviews.map(item =>
                        <div className="user-review" key={`${item.id}`}>
                            <div className="user-review-head">
                                <div className="user-review-stars">
                                    {[1, 2, 3, 4, 5].map(star => <i key={star} className={star <= parseInt(item.rate) ? "on" : ""}>★</i>)}
                                </div>
                                <b>{item.user && `${item.user}`.trim() ? item.user : "Без имени"}</b>
                                <span>{ago(item.createdAt)}</span>
                            </div>
                            {item.comment && <p>{item.comment}</p>}
                            {item.organization && <div className="user-review-member">{item.organization}{item.reply ? " · точка ответила" : ""}</div>}
                        </div>
                    )}
                </div>

            </div>
        )
    }

    /* Everything else the platform knows */
    _extra = () => {

        /* Fields */
        const { data } = this.state

        const extra = data.extra
        const member = data.member
        const cards = data.cards

        const facts = [
            { icon: "notification", text: "Push", value: extra.devices > 0 ? (parseInt(member.notify) === 1 ? `${extra.devices} ${plural(extra.devices, "устройство", "устройства", "устройств")}` : "Выключены") : "Нет устройства", on: extra.devices > 0 && parseInt(member.notify) === 1 },
            { icon: "otp", text: "Последний код", value: extra.lastCode ? ago(extra.lastCode) : "—" },
            { icon: "booking", text: "Записал сам", value: `${data.totals.created.toLocaleString("ru-RU")} ${plural(data.totals.created, "запись", "записи", "записей")}` },
            { icon: "card", text: "Карты", value: cards.length > 0 ? cards.map(card => `•• ${`${card.cardPan || ""}`.slice(-4)}`).join(", ") : "нет", on: cards.length > 0 },
            { icon: "support", text: "Обращения", value: extra.supports > 0 ? `${extra.supports}` : "нет" },
            { icon: "sparkle", text: "Язык · вход", value: `${member.locale === "kk" ? "Қазақша" : "Русский"} · ${member.provided ? member.provided : "—"}` }
        ]

        return (
            <div className="user-section">
                <div className="user-section-title">Ещё о мастере</div>
                <div className="user-extra">
                    {facts.map(fact =>
                        <div className={`user-extra-item ${fact.on ? "on" : ""}`} key={fact.text}>
                            <Icon name={fact.icon} size={16} width={2} />
                            <div>
                                <span>{fact.text}</span>
                                <b>{fact.value}</b>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        )
    }

    _body = () => {

        /* Fields */
        const { status } = this.state

        if (status === "loading") {
            return <div className="wide-modal-loading"><LoadingOutlined /></div>
        }

        if (status !== "ok") {
            return (
                <div className="wide-modal-loading">
                    <Icon name="alert" size={38} width={1.5} />
                    <p>Не удалось загрузить мастера</p>
                </div>
            )
        }

        return (
            <>
                {this._metrics()}
                {this._months()}
                {this._teams()}
                {this._bookings()}
                {this._reviews()}
                {this._extra()}
            </>
        )
    }

    render = () => {

        /* Fields */
        const { visible, item } = this.state

        return (
            <Modal
                isOpen={visible}
                onRequestClose={this.close}
                className="wide-modal large user-modal"
                overlayClassName="wide-modal-overlay"
                closeTimeoutMS={200}
            >

                <div className="wide-modal-header">
                    <h2>Мастер</h2>
                    <div onClick={this.close} className="wide-modal-close">
                        <Icon name="close" size={11} width={2.2} />
                    </div>
                </div>

                {item &&
                    <div className="wide-modal-body">
                        {this._hero()}
                        {this._body()}
                    </div>
                }

            </Modal>
        )
    }
}

export default MasterModal
